import type { CaseFile, ShipmentCase } from 'common/src/api/shipment-cases';

export const caseKinds = {
  observation: 'Observación',
  claim: 'Reclamo',
  incident: 'Siniestro',
};
export const caseStatuses = {
  internal: 'En gestión interna',
  waiting: 'Esperando información',
  closed: 'Cerrado',
};
export const confidenceLabels = {
  firm: 'Firme',
  solid: 'Sólida',
  estimated: 'Estimada',
  reference: 'Referencial',
};
export const caseActions = {
  create: 'Caso ingresado',
  note: 'Comentario',
  assign: 'Encargado reasignado',
  responsible: 'Responsable de la pérdida',
  escalate: 'Tipo actualizado',
  request_input: 'Información solicitada',
  receive_input: 'Respuesta registrada',
  assign_input: 'Encargado de seguimiento actualizado',
  value: 'Valorización registrada',
  close: 'Caso cerrado',
  reopen: 'Caso reabierto',
};
export const outcomes = {
  resolved: 'Resuelto',
  discarded: 'Descartado',
  rejected: 'Rechazado',
};
export const methods = {
  own_vessel: 'Propios · misma nave',
  own_similar: 'Propios · naves similares',
  industry_vessel: 'Otros exportadores · misma nave',
  market: 'Mercado · plataforma',
};
export const sources = {
  official: 'Liquidación oficial documentada',
  reported: 'Precio reportado',
  market: 'Mercado',
  destroyed: 'Fruta destruida',
  unknown: 'Sin información · supuesto cero',
};

/** Label shown for a case: the number given on creation, or CR-<id>. */
export function caseLabel(c: Pick<ShipmentCase, 'id' | 'case_number'>) {
  return c.case_number || `CR-${c.id}`;
}
export function caseTotals(cases: ShipmentCase[]) {
  const cargo = new Map<
    number,
    { kilos: number; risk: number; caseIds: number[] }
  >();
  for (const c of cases.filter((c) => c.status !== 'closed'))
    for (const i of c.items) {
      const key = i.source_box_id;
      const current = cargo.get(key) || { kilos: 0, risk: 0, caseIds: [] };
      current.kilos = Math.max(current.kilos, Number(i.kilos));
      current.risk = Math.max(
        current.risk,
        Number(i.kilos) *
          Math.max(
            0,
            Number(c.valuation?.benchmark_usd_kg || 0) -
              Number(c.valuation?.obtained_usd_kg || 0)
          )
      );
      current.caseIds.push(c.id);
      cargo.set(key, current);
    }
  return {
    kilos: [...cargo.values()].reduce((n, i) => n + i.kilos, 0),
    risk: [...cargo.values()].reduce((n, i) => n + i.risk, 0),
    overlapping: new Set(
      [...cargo.values()]
        .filter((i) => i.caseIds.length > 1)
        .flatMap((i) => i.caseIds)
    ),
    unvalued: cases.filter((c) => c.status !== 'closed' && !c.valuation).length,
  };
}
export function isCaseOverdue(c: ShipmentCase, today: string) {
  return (
    c.status !== 'closed' && c.input_due_on !== null && c.input_due_on < today
  );
}

export function caseFileAppearance(file: CaseFile) {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const image = ['png', 'jpg', 'jpeg', 'webp'].includes(ext);
  if (image)
    return {
      image,
      label: ext.toUpperCase(),
      icon: 'tabler:photo',
      classes: 'bg-default-100 text-default-600',
    };
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return {
      image: false,
      label: ext.toUpperCase(),
      icon: 'tabler:file-spreadsheet',
      classes: 'bg-success-100 text-success-700',
    };
  if (ext === 'pdf')
    return {
      image: false,
      label: 'PDF',
      icon: 'tabler:file-type-pdf',
      classes: 'bg-danger-100 text-danger-700',
    };
  if (['doc', 'docx'].includes(ext))
    return {
      image: false,
      label: ext.toUpperCase(),
      icon: 'tabler:file-type-doc',
      classes: 'bg-secondary-100 text-secondary-700',
    };
  if (ext === 'eml')
    return {
      image: false,
      label: 'EML',
      icon: 'tabler:mail',
      classes: 'bg-warning-100 text-warning-700',
    };
  return {
    image: false,
    label: ext.toUpperCase() || 'FILE',
    icon: 'tabler:file-text',
    classes: 'bg-default-100 text-default-600',
  };
}

export function caseCargoSummary(
  items: ShipmentCase['items'],
  cargo: import('common/src/api/shipment-cases').CaseCargo[]
) {
  const kilos = items.reduce((sum, item) => sum + Number(item.kilos), 0);
  const pallets = new Set(
    items.map((item) => item.snapshot.pallet?.trim()).filter(Boolean)
  ).size;
  const withoutPallet = items.filter(
    (item) => !item.snapshot.pallet?.trim()
  ).length;
  const currentKilos = cargo.reduce(
    (sum, row) => sum + Math.max(0, Number(row.kilos) || 0),
    0
  );
  const currentPallets = new Set(
    cargo.map((row) => row.pallet?.trim()).filter(Boolean)
  ).size;
  const comparable =
    items.length > 0 &&
    items.every((item) => {
      const row = cargo.find(
        (candidate) => candidate.id === item.source_box_id
      );
      return (
        row &&
        Math.abs(Number(row.kilos) - Number(item.kilos)) < 0.000001 &&
        (row.pallet?.trim() || '') === (item.snapshot.pallet?.trim() || '')
      );
    });
  return {
    kilos,
    pallets,
    withoutPallet,
    currentKilos,
    currentPallets,
    comparable,
    percent:
      comparable && currentKilos > 0 ? (kilos / currentKilos) * 100 : null,
  };
}
export function caseInputTiming(
  input: Pick<
    import('common/src/api/shipment-cases').CaseInput,
    'due_on' | 'received_at'
  >,
  today: string
) {
  const difference =
    (Date.parse(`${input.due_on}T00:00:00Z`) -
      Date.parse(`${today}T00:00:00Z`)) /
    86400000;
  const days = Number.isFinite(difference) ? Math.round(difference) : null;
  return {
    days,
    state: input.received_at
      ? ('received' as const)
      : days !== null && days < 0
        ? ('overdue' as const)
        : days === 0
          ? ('today' as const)
          : ('pending' as const),
  };
}

export function myCaseRequests(record: ShipmentCase, userId?: number) {
  if (!userId || record.status === 'closed') return [];
  return (record.pending_requests || []).filter((input) => {
    const owner =
      input.recipient_kind === 'internal'
        ? input.recipient_owner
        : input.followup_owner;
    return !input.received_at && owner?.kind === 'user' && owner.id === userId;
  });
}
